import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Image as ImageIcon, X, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { GlassButton } from '../ui';
import ComponentBrowser from './ComponentBrowser';
import PropertiesPanel, { HOST_STYLED } from './PropertiesPanel';
import PageListPanel from './PageListPanel';
import ImportedSectionList, {
  isHostTextBlank, hostTextToBody, pyLen, HOST_TEXT_BODY_MAX, sectionGroupKey, pageSectionsInOrder,
  HOST_CLONE_ID,
} from './ImportedSectionList';
import ImportedClonePanel from './ImportedClonePanel';
import { errText } from './apiErrorText';
import NewPageModal from './NewPageModal';
import ImportedPageSEOPanel from './ImportedPageSEOPanel';
import ImportedSiteSettingsPanel from './ImportedSiteSettingsPanel';
import SiteCustomCodePanel from './SiteCustomCodePanel';
import { SectionInstance } from './types';
import {
  getSitePatches, saveSitePatches, ImportedEditPatch,
  listSiteSections, insertSiteSection, updateSiteSection, deleteSiteSection, renderSiteSection,
  ImportedSection, ImportedSectionPosition,
  listSitePages, deleteSitePage, listSiteBundlePages, ImportedPage,
  getSiteHeadMeta, HeadMetaEntry,
  cloneSiteBlock, updateSiteCloneText, CloneTextEdit,
} from '../../services/api';

// Mirror SiteBuilder.tsx's API-base resolution (the iframe loads the public /hosted/ route).
const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('/')) return `${window.location.origin}${envUrl}`;
  return envUrl || `${window.location.protocol}//${window.location.hostname}:8001/api`;
};

// One row per edited target. A 'textnode' patch addresses ONE text node of an element, so two of
// them on the same element with different node_index are different rows (later edit of the same
// node replaces the earlier). Keys for text/src/html are unchanged.
const keyOf = (p: { page: string; selector: string; type: string; node_index?: number }) =>
  p.type === 'textnode'
    ? `${p.page}||${p.selector}||${p.type}||${p.node_index}`
    : `${p.page}||${p.selector}||${p.type}`;

/**
 * Editor for imported static sites (source_type="imported_static").
 * Loads the bundle in an iframe with ?edit=1; the server-injected edit-mode script makes text
 * contenteditable and images click-to-swap, posting patches here via postMessage. Patches are
 * stored as an overlay (PUT /patches) — the original bundle files are never modified.
 */
const ImportedSiteEditor: React.FC = () => {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const apiBase = useMemo(getApiBase, []);
  const expectedOrigin = useMemo(() => {
    try { return new URL(apiBase).origin; } catch { return window.location.origin; }
  }, [apiBase]);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Every iframe window THIS editor has created. The iframe is re-keyed on a page change, so a
  // `host-text-input` posted by the old document (on blur, or queued input) can arrive after
  // iframeRef points at the new one; those messages are still ours and must not be dropped.
  // WeakSet: old windows are collected with their iframes, and it dies with the component.
  const ownedWindows = useRef<WeakSet<Window>>(new WeakSet());
  const setIframeEl = useCallback((el: HTMLIFrameElement | null) => {
    iframeRef.current = el;
    if (el?.contentWindow) ownedWindows.current.add(el.contentWindow);
  }, []);
  // Sections whose empty placeholder box in the CURRENT iframe has focus (reported by the edit
  // script). While set, no in-place swap or reload may touch that section's document.
  const editingIds = useRef<Set<string>>(new Set());
  // Sections being deleted (or already deleted): late text messages for them are ignored.
  const deletingIds = useRef<Set<string>>(new Set());
  const patchesRef = useRef<Map<string, ImportedEditPatch>>(new Map());
  const pendingImg = useRef<{ selector: string; page: string } | null>(null);
  // Structural changes reload the iframe, which resets scroll to the top. The iframe reports its
  // scroll position as you move, and we replay a landing spot once it signals ready.
  const lastScrollY = useRef(0);
  const pendingScroll = useRef<{ type: 'section'; id: string } | { type: 'y'; y: number } | null>(null);
  const [count, setCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [imgModal, setImgModal] = useState(false);
  const [imgUrl, setImgUrl] = useState('');
  const [status, setStatus] = useState('');
  const [seedFailed, setSeedFailed] = useState(false);
  // Text/image edits are DOM patches and only persist on Save (section control edits auto-save), so
  // a preview would otherwise silently omit them.
  const [dirty, setDirty] = useState(false);
  // Inserted AIME component sections (control-edited, not DOM-patched).
  const [sections, setSections] = useState<ImportedSection[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Section ids the iframe actually rendered (its `ready` message). null = unknown: an iframe that
  // has not reported yet, or a pre-Phase-2 edit script that sends no ids. Never default it to an
  // empty set, or every section in the list would be badged "Not visible".
  const [visibleIds, setVisibleIds] = useState<Set<string> | null>(null);
  const [browser, setBrowser] = useState<{ anchor_selector: string; position: ImportedSectionPosition; page: string } | null>(null);
  const [iframeKey, setIframeKey] = useState(0);   // bump to reload the iframe after a section change
  // Pages: the bundle's own files (read-only) + the pages AIME generates from their chrome.
  const [bundlePages, setBundlePages] = useState<string[]>([]);
  const [entrypoint, setEntrypoint] = useState('index.html');
  const [generatedPages, setGeneratedPages] = useState<ImportedPage[]>([]);
  const [currentPage, setCurrentPage] = useState('');
  const [newPageOpen, setNewPageOpen] = useState(false);
  // head_meta: BUNDLE HOST pages' SEO overrides (generated pages carry their own on ImportedPage
  // itself — see generatedPages above). Full map, keyed by bundle-relative page path.
  const [headMeta, setHeadMeta] = useState<Record<string, HeadMetaEntry>>({});
  // Both loads are async and race the panel's mount. ImportedPageSEOPanel must not render (and must
  // not be Save-able) until BOTH have resolved (success or failure) — otherwise it seeds itself from
  // {} and Save silently wipes real stored metadata (code review B1/B3), or Save fires with the
  // still-default entrypoint before the real one is known (I10). Set true in both .then and .catch —
  // a failed load still means "no more data is coming", so blank-seeding at that point is correct
  // for DISPLAY. It is NOT correct for Save, which PUTs headMeta back as a whole-map replace — a
  // failed load plus a Save would wipe every other page's stored SEO metadata. headMetaFailed tracks
  // that tail case separately so the panel can disable Save for bundle host pages (code review I12).
  const [headMetaLoaded, setHeadMetaLoaded] = useState(false);
  const [headMetaFailed, setHeadMetaFailed] = useState(false);
  const [bundlePagesLoaded, setBundlePagesLoaded] = useState(false);

  // This tab's in-memory "Copy" clipboard (clone-block): ONE block of THIS page, never persisted
  // (no localStorage, no server, no OS clipboard). Cleared on page switch, Cancel copy and unmount.
  type Clip = { page: string; source_selector: string; source_text: string; tag: string; label: string };
  const [clip, setClip] = useState<Clip | null>(null);
  const clipRef = useRef<Clip | null>(null);
  clipRef.current = clip;

  // False once the component has unmounted. A queued job (the unmount flush) and the head-of-line
  // "waiting" timer outlive the component, so every state update they make is guarded by this.
  // Declared FIRST among the effects: React runs cleanups in declaration order, so by the time the
  // unmount flush below is enqueued this is already false (no waiting timer is armed for it).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const waitTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  useEffect(() => () => {
    waitTimers.current.forEach(t => clearTimeout(t));
    waitTimers.current.clear();
  }, []);

  // ONE per-site write queue for every `inserted_sections` writer. Each of those writes is a server-side
  // read-modify-write of the whole column and two requests from one tab can land on different workers,
  // so un-serialised writes lose updates (type in a copy, click Duplicate: the blur PATCH and the click
  // POST race). A job runs only after the previous one SETTLED; a failing job does not poison the chain.
  const writeChain = useRef<Promise<unknown>>(Promise.resolve());
  const queueDepth = useRef(0);
  const WAITING = 'Waiting for an earlier save to finish…';
  const enqueueWrite = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (queueDepth.current > 0 && mountedRef.current) {
      // Head-of-line visibility: one hung request blocks every later write until axios's timeout.
      timer = setTimeout(() => {
        if (timer) waitTimers.current.delete(timer);
        if (mountedRef.current) setStatus(WAITING);
      }, 3000);
      waitTimers.current.add(timer);
    }
    queueDepth.current += 1;
    const run = writeChain.current.catch(() => {}).then(() => {
      if (timer) { clearTimeout(timer); waitTimers.current.delete(timer); }
      if (mountedRef.current) setStatus(prev => (prev === WAITING ? '' : prev));
      return fn();
    }).finally(() => { queueDepth.current -= 1; });
    writeChain.current = run;
    return run;
  }, []);

  const currentGenerated = generatedPages.find(p => p.page_rel === currentPage) || null;
  // "" -> the bundle entrypoint, which is what /hosted/ serves for an empty path.
  const src = `${apiBase}/site-builder/sites/${siteId}/hosted/${currentPage}?edit=1`;
  // A full reload re-fetches the ENTIRE bundle (the sample site's hero is a 121-frame sequence), so it
  // is reserved for STRUCTURAL changes only — insert / delete / reorder. Control edits swap the single
  // affected section in place via refreshSectionPreview instead.
  const reloadIframe = useCallback(() => { setVisibleIds(null); setIframeKey(k => k + 1); }, []);

  // Whether each section's last known render was EMPTY. In edit mode a blank Host-Styled Text block
  // is served as an empty marked wrapper that the edit script decorates as a "Click to add text"
  // placeholder (no wrapper at all outside edit mode), so the first non-empty render needs one
  // reload to replace the placeholder with real content, and a render that goes empty needs one to
  // bring the placeholder back. Seeded on insert and from the server in refreshSections.
  // undefined = unknown (keep the in-place swap, as before).
  const renderedEmpty = useRef<Record<string, boolean>>({});

  // `savedControls` is passed for Host-Styled Text only: emptiness is then decided from the controls
  // (isHostTextBlank, the server's own rule) — a `<p></p>` body renders non-empty HTML but shows
  // nothing, and swapping it in place would leave a zero-height wrapper instead of the placeholder.
  const refreshSectionPreview = useCallback(async (sectionId: string, savedControls?: Record<string, any>) => {
    // The user is typing in this section's placeholder box right now: swapping or reloading would
    // replace the box and whatever they typed after this save. Their blur sends the final text, which
    // saves again and lands here with editing cleared (renderedEmpty is deliberately left stale so
    // that save performs the empty -> non-empty reload).
    if (editingIds.current.has(sectionId)) return;
    try {
      const html = await renderSiteSection(siteId, sectionId);
      const nowEmpty = savedControls !== undefined ? isHostTextBlank(savedControls) : !(html || '').trim();
      const wasEmpty = renderedEmpty.current[sectionId];
      renderedEmpty.current[sectionId] = nowEmpty;
      if (wasEmpty !== undefined && wasEmpty !== nowEmpty) {
        pendingScroll.current = nowEmpty ? { type: 'y', y: lastScrollY.current } : { type: 'section', id: sectionId };
        setVisibleIds(null);
        setIframeKey(k => k + 1);
        return;
      }
      iframeRef.current?.contentWindow?.postMessage(
        { source: 'aime-imported-editor-parent', kind: 'update-section', section_id: sectionId, html },
        expectedOrigin,
      );
    } catch (e: any) {
      // The save itself already succeeded — only the live preview failed. Say so rather than
      // swallowing it: a silent failure here looks identical to "my edit didn't work".
      setStatus('Saved, but the preview could not refresh — reload to see it.');
    }
  }, [siteId, expectedOrigin]);

  const refreshSections = useCallback(async () => {
    try {
      const r = await listSiteSections(siteId);
      const list = r.sections || [];
      setSections(list);
      // Seed renderedEmpty for host-styled sections loaded from the server, so a block that was
      // already blank when the editor opened (served as the placeholder) reloads on its first fill
      // rather than taking the in-place swap. Existing entries are never overwritten: they reflect
      // the last render this session actually performed.
      list.forEach(s => {
        if (HOST_STYLED.has(s.component_id) && !(s.section_id in renderedEmpty.current)) {
          renderedEmpty.current[s.section_id] = isHostTextBlank(s.controls);
        }
      });
    } catch { /* non-fatal */ }
  }, [siteId]);

  useEffect(() => { refreshSections(); }, [refreshSections]);

  const refreshPages = useCallback(async () => {
    try {
      const r = await listSitePages(siteId);
      setGeneratedPages(r.pages || []);
      return r.pages || [];
    } catch { return []; }
  }, [siteId]);

  useEffect(() => { refreshPages(); }, [refreshPages]);

  useEffect(() => {
    setBundlePagesLoaded(false);
    listSiteBundlePages(siteId)
      .then(r => { setBundlePages(r.pages || []); setEntrypoint(r.entrypoint || 'index.html'); })
      .catch(() => { /* non-fatal — the rail just shows no bundle pages */ })
      .finally(() => setBundlePagesLoaded(true));
  }, [siteId]);

  useEffect(() => {
    setHeadMetaLoaded(false);
    setHeadMetaFailed(false);
    getSiteHeadMeta(siteId)
      .then(r => setHeadMeta(r.head_meta || {}))
      .catch(() => setHeadMetaFailed(true))
      .finally(() => setHeadMetaLoaded(true));
  }, [siteId]);

  // A new document (page change / reload) has no focused placeholder, whatever the old one reported.
  useEffect(() => { editingIds.current.clear(); }, [iframeKey, currentPage]);

  const selectPage = (pageRel: string) => {
    setSelectedId(null);
    setClip(null);   // the clipboard is for THIS page only
    setVisibleIds(null);
    // The entrypoint is served at the bare /hosted/ URL; keep that shape so relative asset paths in
    // the bundle resolve the same way they do for a visitor.
    setCurrentPage(pageRel === entrypoint ? '' : pageRel);
  };
  // What the server calls this page — "" (the entrypoint URL) IS the entrypoint file.
  const currentPageRel = currentPage || entrypoint;

  const selected = sections.find(s => s.section_id === selectedId) || null;
  // Adapt an ImportedSection to the SectionInstance shape PropertiesPanel expects.
  const selectedAsInstance: SectionInstance | null = selected ? {
    section_id: selected.section_id,
    component_id: selected.component_id,
    variant: selected.variant || '',
    controls: selected.controls || {},
  } as SectionInstance : null;

  const handleAddComponent = async (componentId: string, variant?: string, defaults?: Record<string, any>) => {
    if (!browser) return;
    try {
      const created = await enqueueWrite(() => insertSiteSection(siteId, {
        page: browser.page,
        anchor_selector: browser.anchor_selector,
        position: browser.position,
        component_id: componentId,
        variant: variant || '',
        controls: defaults || {},
      }));
      setBrowser(null);
      setVisibleIds(null);   // the iframe reloads below; don't badge the new row "Not visible" meanwhile
      // A Host-Styled Text block starts empty, so it renders as the placeholder (see renderedEmpty).
      if (HOST_STYLED.has(created.component_id)) {
        renderedEmpty.current[created.section_id] = isHostTextBlank(created.controls);
      }
      await refreshSections();
      setSelectedId(created.section_id);
      // Land on the section just added, rather than snapping back to the top of the page.
      pendingScroll.current = { type: 'section', id: created.section_id };
      reloadIframe();
      setStatus(`Added ${componentId}.`);
    } catch (e: any) {
      // Close the component browser first: it is a full-screen overlay that would otherwise hide the
      // reason (e.g. the server's "Anchor not found on page … — reload the editor and choose another
      // spot" 400 for a spot it can't use).
      setBrowser(null);
      setStatus('Add failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // Generated pages have no host DOM to hover between, so there are no "+ Add section here" zones —
  // sections are appended into the page in order. This is the entry point instead.
  const handleAddSectionToPage = () => {
    setBrowser({ anchor_selector: '', position: 'page_content', page: currentPageRel });
  };

  const handleDeletePage = async (page: ImportedPage) => {
    if (!window.confirm(`Delete “${page.title}”? Its sections and its menu link go with it.`)) return;
    try {
      await deleteSitePage(siteId, page.page_id);
      const remaining = await refreshPages();
      await refreshSections();
      if (currentPageRel === page.page_rel) selectPage(entrypoint);
      else reloadIframe();   // the menu link vanished from every page
      setStatus(`Deleted ${page.title}.`);
      return remaining;
    } catch (e: any) {
      setStatus('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handlePageCreated = async (page: ImportedPage) => {
    setNewPageOpen(false);
    await refreshPages();
    selectPage(page.page_rel);
    setStatus(`Created ${page.title}. Add a section to fill it.`);
  };

  // Control edits are debounced. PropertiesPanel fires per keystroke and each PUT carries the WHOLE
  // controls object, so naive per-keystroke PUTs race (lost updates) and remount the iframe per
  // character. Two subtleties this MUST get right:
  //   1. Timers are PER SECTION. One shared timer means editing section B cancels A's un-issued
  //      write and strands A's pending edit forever.
  //   2. A generation token guards the post-await clear: keystrokes that land WHILE a PUT is in
  //      flight must not be discarded by that PUT resolving — if the generation moved, re-flush.
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingControls = useRef<Record<string, Record<string, any>>>({});
  const editGen = useRef<Record<string, number>>({});
  const flushRef = useRef<(sectionId: string) => void>(() => {});
  // Text typed into an empty Host-Styled Text placeholder in the iframe (`host-text-input`). The
  // message listener is registered ONCE (effect deps [expectedOrigin, recordPatch]), so it must never
  // read `sections`/`handleControlChange` from its own closure — those are the first render's
  // (`sections` = []), which would silently drop text for every block inserted after mount. It calls
  // this ref instead, re-assigned every render below.
  const hostTextInputRef = useRef<(sectionId: string, text: string) => void>(() => {});

  const scheduleFlush = (sectionId: string) => {
    if (saveTimers.current[sectionId]) clearTimeout(saveTimers.current[sectionId]);
    saveTimers.current[sectionId] = setTimeout(() => {
      delete saveTimers.current[sectionId];
      flushRef.current(sectionId);
    }, 600);
  };

  const flushSection = async (sectionId: string) => {
    if (deletingIds.current.has(sectionId)) { delete pendingControls.current[sectionId]; return; }
    const payload = pendingControls.current[sectionId];
    if (!payload) return;
    const sentGen = editGen.current[sectionId] || 0;
    try {
      const saved = await enqueueWrite(() => updateSiteSection(siteId, sectionId, { controls: payload }));
      if ((editGen.current[sectionId] || 0) !== sentGen) {
        scheduleFlush(sectionId);   // keystrokes landed mid-PUT — they still need sending
        return;
      }
      delete pendingControls.current[sectionId];
      // Swap just this section — never reload the bundle (unless it crosses empty <-> non-empty).
      // Host-styled: judge emptiness on the server's sanitised controls when it returned them.
      const hostStyled = HOST_STYLED.has(saved?.component_id || '')
        || HOST_STYLED.has(sections.find(s => s.section_id === sectionId)?.component_id || '');
      refreshSectionPreview(sectionId, hostStyled ? (saved?.controls || payload) : undefined);
      setStatus('Section updated.');
    } catch (e: any) {
      setStatus('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
  };
  flushRef.current = flushSection;

  // On unmount: drop the timers but still send anything pending (fire-and-forget), so navigating
  // away immediately after typing doesn't silently lose the last edit.
  // A React unmount is a route change, not a page unload: the queue keeps running, so this flush is
  // QUEUED behind any in-flight write (an unqueued PUT could race it on another worker).
  useEffect(() => () => {
    Object.values(saveTimers.current).forEach(t => clearTimeout(t));
    Object.entries(pendingControls.current).forEach(([sid, controls]) => {
      enqueueWrite(() => updateSiteSection(siteId, sid, { controls })).catch(() => {});
    });
  }, [siteId, enqueueWrite]);

  const handleControlChange = (sectionId: string, key: string, value: any) => {
    const target = sections.find(s => s.section_id === sectionId);
    if (!target) return;
    const controls = { ...(pendingControls.current[sectionId] || target.controls || {}), [key]: value };
    pendingControls.current[sectionId] = controls;
    editGen.current[sectionId] = (editGen.current[sectionId] || 0) + 1;
    setSections(prev => prev.map(s => s.section_id === sectionId ? { ...s, controls } : s));
    scheduleFlush(sectionId);
  };

  // mirrors flushRef; keep in the render body (the onMsg effect deps are [expectedOrigin, recordPatch],
  // so closing over state there would capture first-render state). Re-created every render, so it sees
  // this render's `sections` and `handleControlChange`.
  hostTextInputRef.current = (sectionId: string, text: string) => {
    if (deletingIds.current.has(sectionId)) return;   // delete in progress / done: nothing to save
    const target = sections.find(s => s.section_id === sectionId);
    if (!target || !HOST_STYLED.has(target.component_id)) {
      setStatus("Couldn't save that text — reload the editor and try again.");
      return;
    }
    const body = hostTextToBody(text);
    if (!body) return;   // whitespace only — the iframe doesn't send it; nothing to save
    const len = pyLen(body);
    if (len > HOST_TEXT_BODY_MAX) {
      // Never truncate. The text stays in the placeholder for the user to shorten.
      setStatus(`That text is too long to save (${len.toLocaleString()} of ${HOST_TEXT_BODY_MAX.toLocaleString()} characters allowed, including formatting). Nothing was saved — shorten it and click away again.`);
      return;
    }
    handleControlChange(sectionId, 'body', body);
    setSelectedId(sectionId);
  };

  // Move an inserted section up/down in render order. `order` only matters between sections that share
  // the same spot — (anchor_selector, position): serve sorts by `order`, then each section lands at its
  // OWN anchor, so swapping with a section at another spot changes nothing visible. So swap with the
  // NEAREST same-spot neighbour (sectionGroupKey), and renumber the whole page — filtered exactly as
  // serve and the "Sections on this page" list filter it (a legacy "" row and an entrypoint row both
  // render on the entrypoint and interleave by `order`).
  const moveSection = (sectionId: string, delta: number): Promise<void> => enqueueWrite(async () => {
    // The whole move is ONE queued job that re-reads the sections first: a Duplicate queued before it
    // may have added a row (order min-1 / max+1) the click-time snapshot never saw, and renumbering
    // from stale data could tie with it. The renumbering PUTs stay contiguous inside the job.
    try {
      const fresh: ImportedSection[] = (await listSiteSections(siteId)).sections || [];
      const target = fresh.find(s => s.section_id === sectionId);
      if (!target) return;
      const pageSections = pageSectionsInOrder(fresh, currentPageRel, entrypoint);
      const i = pageSections.findIndex(s => s.section_id === sectionId);
      if (i < 0) return;
      const key = sectionGroupKey(target);
      let j = -1;
      for (let k = i + delta; k >= 0 && k < pageSections.length; k += delta) {
        if (sectionGroupKey(pageSections[k]) === key) { j = k; break; }
      }
      if (j < 0) {
        if (mountedRef.current) setStatus('Only sections added at the same spot can be reordered — nothing to move past here.');
        return;
      }
      [pageSections[i], pageSections[j]] = [pageSections[j], pageSections[i]];
      for (let idx = 0; idx < pageSections.length; idx++) {
        if (pageSections[idx].order !== idx) {
          await updateSiteSection(siteId, pageSections[idx].section_id, { order: idx });
        }
      }
      await refreshSections();
      // Follow the moved section rather than jumping to the top.
      pendingScroll.current = { type: 'section', id: sectionId };
      reloadIframe();
    } catch (e: any) {
      if (mountedRef.current) setStatus('Reorder failed: ' + (e?.response?.data?.detail || e.message));
    }
  });

  // Anchor ID — the in-page link target for this section (#anchor_id). Saved on blur (not per
  // keystroke): it's validated + uniqueness-checked server-side, so a half-typed value would 400.
  const saveAnchorId = async (sectionId: string, value: string) => {
    const current = sections.find(s => s.section_id === sectionId);
    const next = value.trim();
    if (!current || !next || next === (current.anchor_id || '')) return;
    try {
      await enqueueWrite(() => updateSiteSection(siteId, sectionId, { anchor_id: next }));
      await refreshSections();
      setStatus(`Link to this section with #${next}`);
    } catch (e: any) {
      setStatus('Anchor ID: ' + (e?.response?.data?.detail || e.message));
      await refreshSections();   // revert the input to the server's value
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    // Cancel any armed/pending write for this section FIRST. Otherwise typing and then deleting
    // within the debounce window leaves an orphaned timer that PUTs a now-deleted id — the backend
    // 404s and the user sees "Save failed: Section not found" right after a successful delete, and
    // the dangling pending entry fires a second doomed PUT at unmount.
    if (saveTimers.current[sectionId]) {
      clearTimeout(saveTimers.current[sectionId]);
      delete saveTimers.current[sectionId];
    }
    // Snapshot the streamed-but-unsaved text first: if the DELETE fails the section lives on and that
    // text (already shown in the box) must still be saved.
    const savedPending = pendingControls.current[sectionId];
    const savedGen = editGen.current[sectionId];
    delete pendingControls.current[sectionId];
    delete editGen.current[sectionId];
    delete renderedEmpty.current[sectionId];
    // Mark BEFORE the await: the mousedown that started this delete blurred the placeholder and queued
    // a host-text-input that is processed while the DELETE is in flight (or after it). Kept set after
    // success (ids are never reused; a later stale message must stay ignored), cleared on failure.
    deletingIds.current.add(sectionId);
    editingIds.current.delete(sectionId);
    try {
      await enqueueWrite(() => deleteSiteSection(siteId, sectionId));
      setSelectedId(null);
      await refreshSections();
      pendingScroll.current = { type: 'y', y: lastScrollY.current };   // stay put across the reload
      reloadIframe();
    } catch (e: any) {
      deletingIds.current.delete(sectionId);
      if (savedPending && !pendingControls.current[sectionId]) {
        pendingControls.current[sectionId] = savedPending;
        editGen.current[sectionId] = Math.max(editGen.current[sectionId] || 0, savedGen || 0);
        scheduleFlush(sectionId);
      }
      setStatus('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // --- Copy / duplicate a block (clone-block) ------------------------------------------------------
  // The message listener is registered ONCE, so (like hostTextInputRef) it dispatches clone messages
  // through this ref, re-assigned every render below, to see this render's dirty/sections/clip/page.
  const cloneMsgRef = useRef<(kind: string, patch: any, fromCurrent: boolean) => void>(() => {});

  const postToIframe = (msg: Record<string, any>) => {
    iframeRef.current?.contentWindow?.postMessage({ source: 'aime-imported-editor-parent', ...msg }, expectedOrigin);
  };
  // Keep the iframe's Paste buttons in step with the clipboard.
  useEffect(() => {
    postToIframe({ kind: 'clone-clipboard', active: !!clip, tag: clip ? clip.tag : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip]);

  const createClone = (body: { page: string; source_selector: string; source_text: string;
                               anchor_selector: string; position: 'before' | 'after' }, tag: string, isPaste: boolean) => {
    enqueueWrite(() => cloneSiteBlock(siteId, body)).then(async created => {
      setVisibleIds(null);
      await refreshSections();
      setSelectedId(created.section_id);
      pendingScroll.current = { type: 'section', id: created.section_id };
      reloadIframe();
      const d: any = created.dropped;
      const dropped = !!d && ['ids', 'style_rules', 'attributes'].some(k => typeof d[k] === 'number' && d[k] > 0);
      setStatus('Copy added — click its text to change it.'
        + (tag === 'h1' ? ' Pages usually have only one main heading.' : '')
        + (dropped ? " Some of this block's settings weren't copied (an id, unsafe style rules or other attributes), so it may look slightly different." : ''));
    }).catch((e: any) => {
      if (isPaste && e?.response?.status === 409) {
        // The source block changed since it was copied: the clipboard is stale for good.
        setClip(null);
        setStatus('That block has changed since you copied it — copy it again.');
        return;
      }
      setStatus('Copy failed: ' + errText(e));   // a paste 400 (bad spot) keeps the clipboard
    });
  };

  cloneMsgRef.current = (kind: string, patch: any, fromCurrent: boolean) => {
    const isStr = (v: unknown): v is string => typeof v === 'string';
    if (kind === 'clone-text') {
      const sid = patch.section_id;
      if (!isStr(sid) || deletingIds.current.has(sid)) return;
      if (!sections.some(s => s.section_id === sid)) return;
      const edits = patch.edits;
      if (!Array.isArray(edits) || edits.length === 0 || !edits.every((x: any) =>
          x && Number.isInteger(x.node_index) && x.node_index >= 0 && isStr(x.expect) && isStr(x.value))) return;
      const clean: CloneTextEdit[] = edits.map((x: any) => ({ node_index: x.node_index, expect: x.expect, value: x.value }));
      enqueueWrite(() => updateSiteCloneText(siteId, sid, clean)).then(row => {
        setSections(prev => prev.map(s => s.section_id === row.section_id ? row : s));
        setStatus('Text saved.');
      }).catch((e: any) => {
        setStatus('Text not saved: ' + errText(e) + ' — reloading.');
        pendingScroll.current = { type: 'section', id: sid };
        refreshSections();
        reloadIframe();
      });
      return;
    }
    if (!fromCurrent) return;
    if (kind === 'clone-duplicate' || kind === 'clone-copy') {
      if (!isStr(patch.source_selector) || !isStr(patch.source_text) || !isStr(patch.tag)) return;
      if (dirty) { setStatus('Save your text edits first — copies are made from the saved page.'); return; }
      if (patch.page !== currentPageRel) { setStatus('Copy failed: reload the editor and try again.'); return; }
      if (kind === 'clone-copy') {
        const label = isStr(patch.label) ? patch.label : '';
        setClip({ page: currentPageRel, source_selector: patch.source_selector, source_text: patch.source_text,
                  tag: patch.tag, label });
        setStatus(`Copied “${label}”. Hover a spot and click Paste here, or Paste after on another block. (Cancel copy to add sections again.)`);
        return;
      }
      createClone({ page: currentPageRel, source_selector: patch.source_selector, source_text: patch.source_text,
                    anchor_selector: patch.source_selector, position: 'after' }, patch.tag, false);
      return;
    }
    if (kind === 'clone-paste') {
      const c = clipRef.current;
      if (!c) return;
      if (!isStr(patch.anchor_selector) || (patch.position !== 'before' && patch.position !== 'after')) return;
      if (dirty) { setStatus('Save your text edits first — copies are made from the saved page.'); return; }
      if (patch.page !== currentPageRel || c.page !== currentPageRel) {
        setStatus('Copy failed: reload the editor and try again.');
        return;
      }
      createClone({ page: currentPageRel, source_selector: c.source_selector, source_text: c.source_text,
                    anchor_selector: patch.anchor_selector, position: patch.position }, c.tag, true);
    }
  };

  // Seed the map with existing patches so Save (a wholesale replace) never drops prior edits.
  // If this fails we must NOT allow a save — it would overwrite the server's patch list with a
  // partial set and silently drop existing edits.
  useEffect(() => {
    getSitePatches(siteId)
      .then(r => {
        (r.patches || []).forEach(p => patchesRef.current.set(keyOf(p), p));
        setCount(patchesRef.current.size);
      })
      .catch(() => setSeedFailed(true));
  }, [siteId]);

  const recordPatch = useCallback((p: ImportedEditPatch) => {
    // A re-edit of a text node keeps the FIRST-captured `expect` (the pristine text): the iframe shows
    // the patched page, so the text it reports on focus is the previous edit, not the bundle's.
    if (p.type === 'textnode') {
      const prev = patchesRef.current.get(keyOf(p));
      if (prev && prev.expect !== undefined) p = { ...p, expect: prev.expect };
    }
    patchesRef.current.set(keyOf(p), p);
    setCount(patchesRef.current.size);
    setDirty(true);
  }, []);

  // The public /hosted/ route WITHOUT ?edit=1 is the site as visitors see it: patches + inserted
  // sections applied, no edit chrome. Public (unguessable UUID), so a plain new tab works — no token.
  const previewUrl = `${apiBase}/site-builder/sites/${siteId}/hosted/`;

  const openPreview = () => {
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
    if (dirty) setStatus('Preview opened — it shows saved changes; click Save and reload it to include your latest text edits.');
  };

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      // Trust only messages from THIS editor's iframe on the expected origin.
      if (e.origin !== expectedOrigin) return;
      const fromCurrent = !!e.source && e.source === iframeRef.current?.contentWindow;
      // A window this editor created earlier (page change re-keys the iframe) may only deliver the
      // typed-text kinds; everything else must come from the current iframe.
      const fromOwnedOld = !fromCurrent && !!e.source && ownedWindows.current.has(e.source as Window);
      if (!fromCurrent && !fromOwnedOld) return;
      const d: any = e.data || {};
      if (d.source !== 'aime-imported-editor') return;
      const patch = d.patch || {};
      if (fromOwnedOld && patch.kind !== 'host-text-input' && patch.kind !== 'placeholder-blur'
          && patch.kind !== 'clone-text') return;
      if (patch.kind === 'scroll') {
        lastScrollY.current = patch.y || 0;
        return;
      }
      if (patch.kind === 'ready') {
        // Which sections the page actually rendered (drives the list's "Not visible" badge). A
        // pre-Phase-2 edit script sends no ids: stay "unknown" (null), never an empty set.
        setVisibleIds(Array.isArray(patch.section_ids)
          ? new Set<string>(patch.section_ids.filter((x: unknown): x is string => typeof x === 'string'))
          : null);
        // Enable inline typing into empty Host-Styled Text placeholders. Sent on EVERY ready: the
        // flag lives in the iframe document and resets with each reload. Only refs/setters are used
        // in this branch, so the once-registered listener's closure is harmless here.
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'aime-imported-editor-parent', kind: 'editor-caps',
            caps: ['host-text-inline', 'clone-block', 'clone-text'] },
          expectedOrigin,
        );
        // Re-sync the Paste buttons: the flag lives in the iframe document and resets with each reload.
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'aime-imported-editor-parent', kind: 'clone-clipboard',
            active: !!clipRef.current, tag: clipRef.current ? clipRef.current.tag : null },
          expectedOrigin,
        );
        // The reloaded page is live — land where the user was, or on the section they just added.
        const p = pendingScroll.current;
        if (p) {
          iframeRef.current?.contentWindow?.postMessage(
            p.type === 'section'
              ? { source: 'aime-imported-editor-parent', kind: 'scroll-to-section', section_id: p.id }
              : { source: 'aime-imported-editor-parent', kind: 'restore-scroll', y: p.y },
            expectedOrigin,
          );
          pendingScroll.current = null;
        }
        return;
      }
      if (patch.kind === 'patch' && (patch.type === 'text' || patch.type === 'src' || patch.type === 'html')) {
        recordPatch({
          selector: patch.selector,
          type: patch.type,
          value: patch.value,
          page: patch.page || 'index.html',
        });
        setStatus('Edit captured — click Save to keep it.');
      } else if (patch.kind === 'patch' && patch.type === 'textnode'
                 && Number.isInteger(patch.node_index) && patch.node_index >= 0) {
        // Text beside an icon/image: only that text node is patched, the icon is left alone.
        recordPatch({
          selector: patch.selector,
          type: 'textnode',
          node_index: patch.node_index,
          value: patch.value,
          expect: typeof patch.expect === 'string' ? patch.expect : undefined,
          page: patch.page || 'index.html',
        });
        setStatus('Edit captured — click Save to keep it.');
      } else if (patch.kind === 'edit-rejected') {
        setStatus(typeof patch.reason === 'string' && patch.reason
          ? patch.reason
          : "Couldn't save that edit.");
      } else if (patch.kind === 'img-click') {
        pendingImg.current = { selector: patch.selector, page: patch.page || 'index.html' };
        setImgUrl(patch.current || '');
        setImgModal(true);
      } else if (patch.kind === 'insert-zone') {
        setBrowser({
          anchor_selector: patch.anchor_selector,
          position: patch.position === 'after' ? 'after' : 'before',
          page: patch.page || 'index.html',
        });
      } else if (typeof patch.kind === 'string' && patch.kind.startsWith('clone-')) {
        // Via the ref, never directly: see cloneMsgRef.
        cloneMsgRef.current(patch.kind, patch, fromCurrent);
      } else if (patch.kind === 'section-click') {
        setSelectedId(patch.section_id || null);
      } else if (patch.kind === 'placeholder-focus' && typeof patch.section_id === 'string') {
        editingIds.current.add(patch.section_id);
      } else if (patch.kind === 'placeholder-blur' && typeof patch.section_id === 'string') {
        editingIds.current.delete(patch.section_id);
      } else if (patch.kind === 'host-text-input'
                 && typeof patch.section_id === 'string' && typeof patch.text === 'string') {
        // `editing: true` (streamed on each keystroke) only from the live document; anything else,
        // including every message from an old window, means the box is no longer being typed in.
        if (patch.editing === true && fromCurrent) editingIds.current.add(patch.section_id);
        else editingIds.current.delete(patch.section_id);
        // Via the ref, never directly: see hostTextInputRef.
        hostTextInputRef.current(patch.section_id, patch.text);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [expectedOrigin, recordPatch]);

  // "Sections on this page" row click: select it and bring it into view (a section that isn't
  // rendered simply has nothing to scroll to; its properties still open).
  const selectSectionFromList = (sectionId: string) => {
    setSelectedId(sectionId);
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'aime-imported-editor-parent', kind: 'scroll-to-section', section_id: sectionId },
      expectedOrigin,
    );
  };

  const applyImg = () => {
    const pend = pendingImg.current;
    if (pend && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { source: 'aime-imported-editor-parent', kind: 'set-img', selector: pend.selector, value: imgUrl },
        expectedOrigin,
      );
    }
    setImgModal(false);
    pendingImg.current = null;
    setImgUrl('');
  };

  const save = async () => {
    if (seedFailed) return;  // guarded by disabled button; belt-and-suspenders
    setSaving(true);
    try {
      const res = await saveSitePatches(siteId, Array.from(patchesRef.current.values()));
      setDirty(false);
      setStatus(`Saved ${res.count} edit(s).`);
    } catch (e: any) {
      setStatus('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: '1px solid var(--stroke)' }}>
        <GlassButton variant="secondary" onClick={() => navigate('/site-builder')}>
          <ArrowLeft size={16} /> Back
        </GlassButton>
        <div style={{ fontWeight: 600, color: 'var(--text)' }}>Imported Site Editor</div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: seedFailed ? 'var(--danger, #dc2626)' : 'var(--text-secondary)', fontSize: 13 }}>
            {seedFailed ? 'Could not load existing edits — reload before editing (saving is disabled to avoid data loss).' : (status || `${count} edit(s)`)}
          </span>
          {clip && (
            <GlassButton variant="secondary" onClick={() => { setClip(null); setStatus('Copy cancelled.'); }}>
              Cancel copy
            </GlassButton>
          )}
          <GlassButton variant="primary" onClick={save} disabled={saving || seedFailed}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save'}
          </GlassButton>
          <GlassButton
            variant="secondary"
            onClick={openPreview}
            title={(dirty ? 'Opens the saved site in a new tab — Save first to include your latest text edits'
                          : 'Open the site in a new tab, as visitors see it')
                   + '. Custom code doesn\'t run in this preview.'}
          >
            <ExternalLink size={16} /> Preview
          </GlassButton>
        </div>
      </div>
      <div style={{ padding: '6px 20px', fontSize: 12, color: 'var(--text-secondary)', background: 'var(--panel)' }}>
        {currentGenerated ? (
          <>
            This page is built by AiMe using <strong>{currentGenerated.chrome_donor}</strong>’s menu, footer and fonts.
            Use <strong>Add section</strong> to build it up; sections stack in order. To change the menu or footer,
            edit them on <strong>{currentGenerated.chrome_donor}</strong> — the change follows through to here.
          </>
        ) : (
          <>
            Click any text to edit it, or an image to replace it. Hover between blocks for <strong>+ Add section here</strong>.
            Click an added section to edit its properties. Everything is saved as an overlay — the original files are never changed.
            {' '}A new Host-Styled Text block shows a box you can type into. Every section on this page is listed under <strong>Sections on this page</strong> on the left.
            {' '}Hover any block for <strong>Duplicate</strong> or <strong>Copy</strong> — a copy matches the page&rsquo;s own style.
          </>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <PageListPanel
          bundlePages={bundlePages}
          generatedPages={generatedPages}
          currentPage={currentPageRel}
          entrypoint={entrypoint}
          headMeta={headMeta}
          onSelect={selectPage}
          onNewPage={() => setNewPageOpen(true)}
          onDeletePage={handleDeletePage}
          onAddSection={handleAddSectionToPage}
        >
          <ImportedSectionList
            sections={sections}
            currentPageRel={currentPageRel}
            entrypoint={entrypoint}
            selectedId={selectedId}
            visibleIds={visibleIds}
            onSelect={selectSectionFromList}
            onMove={moveSection}
            onDelete={handleDeleteSection}
          />
        </PageListPanel>
        <iframe
          key={`${iframeKey}-${currentPage}`}
          ref={setIframeEl}
          title="Imported site preview"
          src={src}
          style={{ flex: 1, border: 'none', height: '100%' }}
        />
        {selectedAsInstance ? (
          <div style={{ width: 320, borderLeft: '1px solid var(--stroke)', overflowY: 'auto', background: 'var(--panel)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--stroke)' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Section properties</span>
              <div style={{ display: 'flex', gap: 2 }}>
                <button onClick={() => moveSection(selectedAsInstance.section_id, -1)} title="Move up"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
                  <ArrowUp size={15} />
                </button>
                <button onClick={() => moveSection(selectedAsInstance.section_id, 1)} title="Move down"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
                  <ArrowDown size={15} />
                </button>
                <button onClick={() => setSelectedId(null)} title="Close"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}>
                  <X size={16} />
                </button>
              </div>
            </div>
            {selected?.component_id === HOST_CLONE_ID ? (
              <ImportedClonePanel section={selected} onDelete={handleDeleteSection} />
            ) : (<>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--stroke)' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Anchor ID — link to this section with <code>#{selected?.anchor_id || ''}</code>
              </label>
              <input
                key={selected?.section_id + (selected?.anchor_id || '')}
                defaultValue={selected?.anchor_id || ''}
                onBlur={e => saveAnchorId(selectedAsInstance.section_id, e.target.value)}
                placeholder="e.g. faq"
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12, boxSizing: 'border-box',
                  borderRadius: 'var(--radius-sm)', border: '1px solid var(--stroke)',
                  background: 'var(--panel-strong)', color: 'var(--text)',
                }}
              />
            </div>
            <PropertiesPanel
              section={selectedAsInstance}
              siteProjectId={siteId}
              onControlChange={handleControlChange}
              onDeleteSection={handleDeleteSection}
              onRegenerateSection={() => { /* not supported for imported sites */ }}
              allowRegenerate={false}   /* AI regenerate assumes a component-based site_document */
              schemaFor="imported_all"  /* every id insertable on ANY imported page, incl. imported_only */
              embedded
            />
            </>)}
          </div>
        ) : (
          <div style={{ width: 320, borderLeft: '1px solid var(--stroke)', overflowY: 'auto', background: 'var(--panel)' }}>
            {headMetaLoaded && bundlePagesLoaded ? (
              <ImportedPageSEOPanel
                key={currentPageRel}
                siteId={siteId}
                pageRel={currentPageRel}
                generatedPage={currentGenerated}
                headMeta={headMeta}
                headMetaFailed={headMetaFailed}
                isEntrypoint={currentPageRel === entrypoint}
                onGeneratedPageSaved={updated => {
                  setGeneratedPages(prev => prev.map(p => p.page_id === updated.page_id ? updated : p));
                  setStatus('SEO saved.');
                }}
                onHeadMetaSaved={map => {
                  setHeadMeta(map);
                  setStatus('SEO saved.');
                }}
              />
            ) : (
              <div style={{ padding: 16, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Loading SEO settings…
              </div>
            )}
            {/* Site-level tracking code (Google tag/GTM/Meta Pixel). Loads its own data. */}
            <SiteCustomCodePanel siteId={siteId} onStatus={setStatus} isImported />
            {/* Loads its own data, so it does not wait on headMetaLoaded. Keep this LAST in the pane. */}
            <ImportedSiteSettingsPanel siteId={siteId} onStatus={setStatus} />
          </div>
        )}
      </div>

      {browser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', width: 460, maxWidth: '92%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--stroke)' }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>Add a section</span>
              <button onClick={() => setBrowser(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* A generated page can take the runtime-coupled blog components; a host page cannot
                  (nothing sets their render context there, so they'd render empty). The backend
                  enforces this — the filter just avoids offering what would 400. */}
              <ComponentBrowser
                filterFor={browser.position === 'page_content' ? 'imported_page' : 'imported'}
                onAddComponent={handleAddComponent}
              />
            </div>
          </div>
        </div>
      )}

      {newPageOpen && (
        <NewPageModal
          siteId={siteId}
          hasBlogIndex={generatedPages.some(p => p.kind === 'blog_index')}
          onClose={() => setNewPageOpen(false)}
          onCreated={handlePageCreated}
        />
      )}

      {imgModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', padding: 24, width: 420, maxWidth: '90%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: 'var(--text)', fontWeight: 600 }}>
              <ImageIcon size={18} /> Replace image
            </div>
            <input
              value={imgUrl}
              onChange={e => setImgUrl(e.target.value)}
              placeholder="Image URL (e.g. assets/social/photo.webp)"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--stroke)', background: 'var(--panel)', color: 'var(--text)', marginBottom: 16, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <GlassButton variant="secondary" onClick={() => { setImgModal(false); pendingImg.current = null; }}>Cancel</GlassButton>
              <GlassButton variant="primary" onClick={applyImg}>Apply</GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportedSiteEditor;
